import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
    AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { errorText } from '../../lib/apiErrors';

const API = process.env.REACT_APP_BACKEND_URL || '';

export const NOT_IN_PIPELINE = 'Not in pipeline';

export const leadName = (c) => [c?.first_name, c?.last_name].filter(Boolean).join(' ') || c?.full_name || 'this contact';

/**
 * Take a contact out of the Lead Pipeline (#905), confirmed in the app's own
 * dialog. Sends `PUT /contacts/{id}/pipeline-stage {stage: null}`, which clears
 * the stage (and its time-in-stage), logs "→ (none)" and keeps the contact.
 * Used by the Lead Pipeline card's "Not in pipeline" option (instant mode) and
 * the Lead Pipeline drawer's Remove action.
 *
 * Pattern: components/calendar/DestructiveEventDialog.jsx — controlled `open`,
 * dismissal blocked while the request is in flight, and the action
 * preventDefault()s so the dialog stays open until the request settles.
 */
export default function RemoveFromPipelineDialog({ open, contactId, name, getAuthHeader, onCancel, onRemoved }) {
    const [pending, setPending] = useState(false);

    const remove = async (e) => {
        e.preventDefault();
        setPending(true);
        try {
            await axios.put(`${API}/api/contacts/${contactId}/pipeline-stage`, { stage: null }, { headers: getAuthHeader() });
            toast.success(`Removed ${name} from the Lead Pipeline`);
            onRemoved?.(contactId);
        } catch (err) {
            toast.error(errorText(err, 'Failed to remove from the Lead Pipeline'));
        } finally {
            setPending(false);
        }
    };

    return (
        <AlertDialog open={open} onOpenChange={(next) => { if (!next && !pending) onCancel(); }}>
            <AlertDialogContent className="rounded-[14px]" data-testid="lp-remove-dialog">
                <AlertDialogHeader>
                    <AlertDialogTitle>Remove {name} from the Lead Pipeline?</AlertDialogTitle>
                    <AlertDialogDescription>
                        Their contact record is kept. Owner, coach, tags and history stay as they are, and you can add them back by choosing a stage.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel disabled={pending} data-testid="lp-remove-cancel">Keep in pipeline</AlertDialogCancel>
                    <AlertDialogAction
                        onClick={remove}
                        disabled={pending}
                        className="bg-[#A3382B] hover:bg-[#8A2E23] text-white"
                        data-testid="lp-remove-confirm"
                    >
                        {pending ? 'Removing…' : 'Remove'}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
